import { useEffect, useState, useRef } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import {
  DownloadSimple,
  UploadSimple,
  CloudArrowUp,
  CheckCircle,
  XCircle,
  WarningCircle,
  CircleNotch,
  ArrowRight,
  ArrowLeft,
  Info,
  ShieldWarning,
  FileXls,
} from "@phosphor-icons/react";

const STEPS = ["Download Template", "Upload & Validate", "Push to Aroflo"];

function StepHeader({ step }) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="wizard-steps">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm font-semibold ${
              i === step ? "bg-[#0A0A0A] text-white" : i < step ? "bg-[#059669]/10 text-[#059669]" : "bg-neutral-100 text-neutral-500"
            }`}
          >
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${i === step ? "bg-white text-[#0A0A0A]" : i < step ? "bg-[#059669] text-white" : "bg-neutral-300 text-white"}`}>
              {i < step ? "✓" : i + 1}
            </span>
            {label}
          </div>
          {i < STEPS.length - 1 && <ArrowRight size={14} className="text-neutral-300" />}
        </div>
      ))}
    </div>
  );
}

export default function AssetUpdater() {
  const [step, setStep] = useState(0);
  const [schema, setSchema] = useState(null);
  const [capability, setCapability] = useState(null);
  const [apiVersion, setApiVersion] = useState("v2");
  const [businessUnit, setBusinessUnit] = useState("Automated Buildings");

  // upload state
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);

  // push state
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => {
    api.get("/assets/schema").then((r) => setSchema(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setCapability(null);
    api.get("/assets/write-capability", { params: { api_version: apiVersion } }).then((r) => setCapability(r.data)).catch(() => {});
  }, [apiVersion]);

  const downloadTemplate = async (demo) => {
    const url = `${API}/assets/template?business_unit=${encodeURIComponent(businessUnit)}&demo=${demo ? "true" : "false"}`;
    // fetch with credentials then trigger download
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `asset-data-template-${businessUnit.replace(/\s+/g, "_")}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setValidating(true);
    setValidateError("");
    setRows(null);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assets/validate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows);
      setSummary(data.summary);
    } catch (err) {
      setValidateError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setValidating(false);
    }
  };

  const validRows = (rows || []).filter((r) => r.valid);

  const push = async (dryRun) => {
    setPushing(true);
    setPushResult(null);
    try {
      const payload = {
        dry_run: dryRun,
        api_version: apiVersion,
        rows: validRows.map((r) => ({ asset_number: r.asset_number, asset_name: r.asset_name, fields: r.fields })),
      };
      const { data } = await api.post("/assets/push", payload);
      setPushResult(data);
    } catch (err) {
      setValidateError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <Layout>
      <div data-testid="asset-updater-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Phase 2 · Data Editing</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
          Bulk Asset Updater
        </h1>
        <p className="mt-3 max-w-2xl text-base text-neutral-600">
          Update Aroflo asset custom fields with <span className="font-semibold">different values per asset</span>, keyed by
          Asset Number — what the standard Aroflo bulk override can't do.
        </p>

        {/* API selector */}
        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="api-version-selector">
          <span className="text-sm font-semibold text-[#0A0A0A]">Upload via:</span>
          {[
            { id: "v2", label: "Aroflo API (v2)", sub: "Bearer token" },
            { id: "v1", label: "Aroflo API (v1)", sub: "HMAC-SHA512" },
          ].map((opt) => (
            <button key={opt.id} data-testid={`api-version-${opt.id}`} onClick={() => setApiVersion(opt.id)}
              className={`flex flex-col items-start rounded-sm border px-4 py-2 text-left transition-all duration-200 ${apiVersion === opt.id ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}>
              <span className={`text-sm font-semibold ${apiVersion === opt.id ? "text-[#0055FF]" : "text-[#0A0A0A]"}`}>{opt.label}</span>
              <span className="text-xs text-neutral-500">{opt.sub}</span>
            </button>
          ))}
        </div>

        {/* Capability banner */}
        {capability && !capability.writable && (
          <div className="mt-5 flex items-start gap-3 border border-[#D97706]/40 bg-[#D97706]/5 p-4 rounded-sm" data-testid="capability-warning">
            <ShieldWarning size={22} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
            <div className="text-sm text-neutral-700">
              <p className="font-semibold text-[#0A0A0A]">Heads up: Aroflo currently blocks asset writes via {capability.api_version === "v1" ? "Aroflo API (v1)" : "Aroflo API (v2)"}.</p>
              {capability.note ? (
                <p className="mt-1">
                  {capability.note} You can still build & validate the per-asset update file now; once Aroflo enables asset writes,
                  the same "Push" button will write the values live.
                </p>
              ) : (
                <p className="mt-1">
                  Live check — Assets allow <code className="rounded bg-neutral-100 px-1 font-mono">{capability.asset_methods}</code> (read-only),
                  while Tasks allow <code className="rounded bg-neutral-100 px-1 font-mono">{capability.task_methods}</code>.
                  You can still build & validate the per-asset update file now; once Aroflo enables asset <code>PATCH</code> on the token,
                  the same "Push" button will write the values live.
                </p>
              )}
            </div>
          </div>
        )}

        <StepHeader step={step} />

        {/* STEP 0: Download */}
        {step === 0 && (
          <div className="mt-6 border border-neutral-200 bg-white p-6 shadow-sm rounded-sm" data-testid="step-download">
            <h2 className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">1. Download the template</h2>
            <p className="mt-2 text-sm text-neutral-600">
              One business unit per spreadsheet. The template includes an Instructions sheet with the 1-5 rating guides.
              Keep the <span className="font-semibold">Asset Number</span> column — it's the key used to match each row to the right Aroflo asset.
            </p>
            <div className="mt-4 max-w-sm">
              <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Business unit</label>
              <input
                data-testid="business-unit-input"
                value={businessUnit}
                onChange={(e) => setBusinessUnit(e.target.value)}
                className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
                placeholder="e.g. Automated Buildings"
              />
            </div>

            {schema && (
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {schema.custom_fields.map((f) => (
                  <div key={f.key} className="border border-neutral-200 bg-neutral-50 p-3 rounded-sm" data-testid={`field-${f.key}`}>
                    <p className="text-sm font-bold text-[#0A0A0A]">{f.label}</p>
                    <p className="text-xs text-neutral-500">{f.column}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="download-template-button"
                onClick={() => downloadTemplate(false)}
                className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC]"
              >
                <DownloadSimple size={18} weight="bold" /> Download blank template
              </button>
              <button
                data-testid="download-demo-button"
                onClick={() => downloadTemplate(true)}
                className="flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-5 py-2.5 text-base font-semibold text-[#0A0A0A] transition-all duration-200 hover:bg-neutral-50"
              >
                <FileXls size={18} weight="bold" /> Download with demo rows
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                data-testid="goto-upload-button"
                onClick={() => setStep(1)}
                className="flex items-center gap-2 rounded-sm bg-[#0A0A0A] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-neutral-800"
              >
                Next: Upload <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 1: Upload & Validate */}
        {step === 1 && (
          <div className="mt-6 border border-neutral-200 bg-white p-6 shadow-sm rounded-sm" data-testid="step-upload">
            <h2 className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">2. Upload & validate</h2>
            <p className="mt-2 text-sm text-neutral-600">Upload the completed .xlsx or .csv. Each row is checked before anything is sent to Aroflo.</p>

            <div className="mt-4">
              <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onFile} className="hidden" data-testid="file-input" />
              <button
                data-testid="choose-file-button"
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 rounded-sm border-2 border-dashed border-neutral-300 bg-neutral-50 px-6 py-8 text-base font-semibold text-neutral-700 transition-all duration-200 hover:border-[#0055FF] hover:bg-[#0055FF]/5 w-full justify-center"
              >
                <UploadSimple size={22} weight="bold" />
                {fileName ? `Selected: ${fileName}` : "Choose spreadsheet (.xlsx / .csv)"}
              </button>
            </div>

            {validating && (
              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-600"><CircleNotch size={18} className="animate-spin text-[#0055FF]" /> Validating…</div>
            )}
            {validateError && (
              <div className="mt-4 rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-3.5 py-2.5 text-sm text-[#DC2626]" data-testid="validate-error">{validateError}</div>
            )}

            {summary && (
              <div className="mt-5">
                <div className="flex flex-wrap gap-3" data-testid="validate-summary">
                  <span className="rounded-sm bg-neutral-100 px-3 py-1.5 text-sm font-semibold text-[#0A0A0A]">{summary.total} rows</span>
                  <span className="rounded-sm bg-[#059669]/10 px-3 py-1.5 text-sm font-semibold text-[#059669]">{summary.valid} valid</span>
                  <span className="rounded-sm bg-[#DC2626]/10 px-3 py-1.5 text-sm font-semibold text-[#DC2626]">{summary.invalid} with errors</span>
                </div>

                <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-sm">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                        <th className="px-3 py-2.5 text-left">Row</th>
                        <th className="px-3 py-2.5 text-left">Asset #</th>
                        <th className="px-3 py-2.5 text-left">Asset Name</th>
                        <th className="px-3 py-2.5 text-right">Cond</th>
                        <th className="px-3 py-2.5 text-right">Risk</th>
                        <th className="px-3 py-2.5 text-right">Env</th>
                        <th className="px-3 py-2.5 text-right">Util</th>
                        <th className="px-3 py-2.5 text-right">Life</th>
                        <th className="px-3 py-2.5 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.row} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`validate-row-${r.row}`}>
                          <td className="px-3 py-2.5 text-neutral-500">{r.row}</td>
                          <td className="px-3 py-2.5 font-mono font-semibold">{r.asset_number || "—"}</td>
                          <td className="max-w-[220px] truncate px-3 py-2.5">{r.asset_name || "—"}</td>
                          <td className="px-3 py-2.5 text-right">{r.fields.condition ?? "—"}</td>
                          <td className="px-3 py-2.5 text-right">{r.fields.risk ?? "—"}</td>
                          <td className="px-3 py-2.5 text-right">{r.fields.environment ?? "—"}</td>
                          <td className="px-3 py-2.5 text-right">{r.fields.utilisation ?? "—"}</td>
                          <td className="px-3 py-2.5 text-right">{r.fields.remaining_life ?? "—"}</td>
                          <td className="px-3 py-2.5">
                            {r.valid ? (
                              <span className="inline-flex items-center gap-1 text-[#059669]"><CheckCircle size={15} weight="fill" /> OK</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#DC2626]" title={r.errors.join(" ")}><XCircle size={15} weight="fill" /> {r.errors[0]}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button data-testid="back-to-download-button" onClick={() => setStep(0)} className="flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-5 py-2.5 text-base font-semibold text-[#0A0A0A] hover:bg-neutral-50">
                <ArrowLeft size={18} weight="bold" /> Back
              </button>
              <button
                data-testid="goto-push-button"
                disabled={!validRows.length}
                onClick={() => { setStep(2); setPushResult(null); }}
                className="flex items-center gap-2 rounded-sm bg-[#0A0A0A] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-neutral-800 disabled:opacity-50"
              >
                Next: Push {validRows.length ? `(${validRows.length})` : ""} <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Push */}
        {step === 2 && (
          <div className="mt-6 border border-neutral-200 bg-white p-6 shadow-sm rounded-sm" data-testid="step-push">
            <h2 className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">3. Push to Aroflo</h2>
            <p className="mt-2 text-sm text-neutral-600">
              {validRows.length} valid asset{validRows.length === 1 ? "" : "s"} ready. Run a dry-run to preview the exact per-asset
              payloads, or push live to write them into Aroflo.
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="dry-run-button" onClick={() => push(true)} disabled={pushing} className="flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-5 py-2.5 text-base font-semibold text-[#0A0A0A] hover:bg-neutral-50 disabled:opacity-50">
                {pushing ? <CircleNotch size={18} className="animate-spin" /> : <Info size={18} weight="bold" />} Dry run (preview)
              </button>
              <button data-testid="push-live-button" onClick={() => push(false)} disabled={pushing} className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white hover:bg-[#0044CC] disabled:opacity-50">
                {pushing ? <CircleNotch size={18} className="animate-spin" /> : <CloudArrowUp size={18} weight="bold" />} Push live to Aroflo
              </button>
            </div>

            {pushResult && (
              <div className="mt-6" data-testid="push-result">
                <div className="flex flex-wrap gap-3">
                  <span className="rounded-sm bg-neutral-100 px-3 py-1.5 text-sm font-semibold">{pushResult.summary.total} processed</span>
                  {pushResult.dry_run ? (
                    <span className="rounded-sm bg-[#0055FF]/10 px-3 py-1.5 text-sm font-semibold text-[#0055FF]">Dry run — nothing sent</span>
                  ) : (
                    <>
                      <span className="rounded-sm bg-[#059669]/10 px-3 py-1.5 text-sm font-semibold text-[#059669]">{pushResult.summary.pushed} updated</span>
                      <span className="rounded-sm bg-[#D97706]/10 px-3 py-1.5 text-sm font-semibold text-[#D97706]">{pushResult.summary.blocked} blocked</span>
                      <span className="rounded-sm bg-[#DC2626]/10 px-3 py-1.5 text-sm font-semibold text-[#DC2626]">{pushResult.summary.failed} failed</span>
                    </>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  {pushResult.results.map((r, i) => (
                    <div key={`${r.asset_number}-${i}`} className="flex items-start justify-between gap-4 border border-neutral-200 bg-neutral-50 px-4 py-3 rounded-sm" data-testid={`push-row-${i}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0A0A0A]">
                          <span className="font-mono">{r.asset_number}</span> · {r.asset_name || "—"}
                        </p>
                        <p className="mt-0.5 text-xs text-neutral-600">{r.message}</p>
                        {r.payload && (
                          <p className="mt-1 font-mono text-xs text-neutral-500">{JSON.stringify(r.payload.customFields)}</p>
                        )}
                      </div>
                      <span className={`shrink-0 rounded-sm px-2 py-1 text-xs font-semibold ${
                        r.status === "updated" ? "bg-[#059669]/10 text-[#059669]" :
                        r.status === "dry_run" ? "bg-[#0055FF]/10 text-[#0055FF]" :
                        r.status === "blocked" ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#DC2626]/10 text-[#DC2626]"
                      }`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-start">
              <button data-testid="back-to-upload-button" onClick={() => setStep(1)} className="flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-5 py-2.5 text-base font-semibold text-[#0A0A0A] hover:bg-neutral-50">
                <ArrowLeft size={18} weight="bold" /> Back
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
